import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
} from '@mui/material'
import { Send, Preview } from '@mui/icons-material'
import { useEmailManagement } from '../../../hooks/useEmailManagement'
import { EMAIL_TYPE_OPTIONS, getEmailTypeLabel } from '../../../utils/emailTypeOptions'
import { sortTemplatesByDisplayOrder } from '../../../utils/templateOrder'

const RECIPIENT_TYPES = [
  { value: 'TEAM_ONLY', label: 'Team Only (official email addresses)' },
  { value: 'ALL_USERS', label: 'All Users' },
  { value: 'CUSTOM', label: 'Custom' },
]

const CONTENT_TYPES = [
  { value: 'HTML', label: 'HTML' },
  { value: 'MARKDOWN', label: 'Markdown' },
]

const INITIAL_FORM_DATA = {
  subject: '',
  content: '',
  contentType: 'HTML',
  emailType: 'NO_REPLY',
  recipientType: 'TEAM_ONLY',
  templateId: '',
  customRecipients: '',
  ccEmails: '',
  bccEmails: '',
}

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
  success: '#43af76',
}

const TEMPLATE_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      width: { xs: 'calc(100vw - 32px)', sm: 620 },
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 420,
      overflowX: 'auto',
    },
  },
  MenuListProps: {
    sx: {
      width: 'max-content',
      minWidth: '100%',
      '& .MuiMenuItem-root': {
        py: 1.25,
      },
    },
  },
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const parseEmails = (value) => {
  if (!value) return []

  return value
    .split(/[\n,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

const getUniqueEmails = (emails) => {
  const seen = new Set()
  return emails.filter((email) => {
    const normalized = email.toLowerCase()
    if (seen.has(normalized)) return false
    seen.add(normalized)
    return true
  })
}

const getInvalidEmails = (emails) =>
  emails.filter((email) => !EMAIL_REGEX.test(email))

const getNonEmptyTemplateVariables = (variables) =>
  Object.fromEntries(
    Object.entries(variables || {}).filter(([, value]) =>
      typeof value === 'string' ? value.trim() !== '' : value != null,
    ),
  )

const detectTemplateVariables = (text) => {
  if (!text) return []
  const regex = /\{\{(\w+)\}\}/g
  const matches = [...text.matchAll(regex)]
  return [...new Set(matches.map((m) => m[1]))]
}

const EmailComposer = ({
  showNotification,
  onDraftStateChange,
  templateSelectionRequest,
}) => {
  const { templates, loadTemplates, sendEmail, getRecipientCount } =
    useEmailManagement()
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  const [recipientCount, setRecipientCount] = useState(0)
  const [showPreview, setShowPreview] = useState(false)
  const [sending, setSending] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    recipientCount: 0,
  })
  const [emptyVariablesDialog, setEmptyVariablesDialog] = useState({
    open: false,
    variables: [],
  })
  const [templateVariables, setTemplateVariables] = useState({
    // userName: '',
    // userEmail: '',
    // Add more as needed
  })

  const replaceVariablesInPreview = (text) => {
    if (!text) return text

    let result = text
    Object.keys(templateVariables).forEach((varName) => {
      const value = templateVariables[varName] || `[${varName}]` // Show placeholder if empty
      result = result.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), value)
    })
    return result
  }

  const orderedTemplates = useMemo(() => {
    return sortTemplatesByDisplayOrder(templates)
  }, [templates])

  const handleTemplateVarChange = (varName) => (e) => {
    setTemplateVariables((prev) => ({
      ...prev,
      [varName]: e.target.value,
    }))
  }

  const applyTemplateById = useCallback(
    (templateId) => {
      if (templateId === '' || templateId == null) {
        setFormData((prev) => ({ ...prev, templateId: '' }))
        setTemplateVariables({})
        return true
      }

      const template = templates.find((t) => String(t.id) === String(templateId))
      if (!template) return false

      setFormData((prev) => ({
        ...prev,
        templateId: template.id,
        subject: template.subject || '',
        content: template.content || '',
        contentType: template.contentType || 'HTML',
        emailType: template.emailType || 'NO_REPLY',
      }))

      const contentVars = detectTemplateVariables(template.content)
      const subjectVars = detectTemplateVariables(template.subject)
      const allVars = [...new Set([...contentVars, ...subjectVars])]
      const initialVars = {}

      allVars.forEach((varName) => {
        initialVars[varName] = ''
      })
      setTemplateVariables(initialVars)

      return true
    },
    [templates],
  )

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  useEffect(() => {
    if (templateSelectionRequest?.templateId == null) return
    applyTemplateById(templateSelectionRequest.templateId)
  }, [templateSelectionRequest, applyTemplateById])

  useEffect(() => {
    if (typeof onDraftStateChange !== 'function') return

    const isFormDataDirty = Object.keys(INITIAL_FORM_DATA).some(
      (key) => formData[key] !== INITIAL_FORM_DATA[key],
    )
    const hasTemplateVariableValues = Object.values(templateVariables).some(
      (value) => (value || '').trim() !== '',
    )

    onDraftStateChange(isFormDataDirty || hasTemplateVariableValues)
  }, [formData, templateVariables, onDraftStateChange])

  useEffect(() => {
    // Load recipient count when recipient type changes
    const loadCount = async () => {
      if (formData.recipientType === 'CUSTOM') {
        const uniqueCustomRecipients = getUniqueEmails(
          parseEmails(formData.customRecipients),
        )
        setRecipientCount(uniqueCustomRecipients.length)
        return
      }

      const count = await getRecipientCount(formData.recipientType)
      setRecipientCount(count)
    }
    loadCount()
  }, [formData.recipientType, formData.customRecipients, getRecipientCount])

  const handleChange = (field) => (e) => {
    const value = e.target.value
    if (field === 'templateId') {
      const templateApplied = applyTemplateById(value)
      if (!templateApplied) {
        setFormData((prev) => ({ ...prev, templateId: value }))
      }
      return
    }

    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSend = async () => {
    // Validate
    if (!formData.subject.trim()) {
      showNotification('Subject is required', 'error')
      return
    }
    if (!formData.content.trim()) {
      showNotification('Content is required', 'error')
      return
    }

    if (formData.recipientType === 'CUSTOM') {
      const customRecipients = getUniqueEmails(parseEmails(formData.customRecipients))
      const invalidEmails = getInvalidEmails(customRecipients)

      if (customRecipients.length === 0) {
        showNotification('Please add at least one custom recipient email', 'error')
        return
      }

      if (invalidEmails.length > 0) {
        showNotification(
          `Invalid email(s): ${invalidEmails.slice(0, 3).join(', ')}${invalidEmails.length > 3 ? '...' : ''}`,
          'error',
        )
        return
      }
    }

    // Check if template variables are filled
    if (formData.templateId && Object.keys(templateVariables).length > 0) {
      const emptyVars = Object.keys(templateVariables).filter(
        (key) => !templateVariables[key],
      )
      if (emptyVars.length > 0) {
        setEmptyVariablesDialog({
          open: true,
          variables: emptyVars,
        })
        return
      }
    }

    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      recipientCount: recipientCount,
    })
  }

  const handleProceedWithEmptyVariables = () => {
    setEmptyVariablesDialog({ open: false, variables: [] })
    setConfirmDialog({
      open: true,
      recipientCount: recipientCount,
    })
  }

  const handleConfirmSend = async () => {
    setConfirmDialog({ open: false, recipientCount: 0 })
    setSending(true)

    try {
      const customRecipients = getUniqueEmails(parseEmails(formData.customRecipients))
      const nonEmptyTemplateVariables = getNonEmptyTemplateVariables(
        templateVariables,
      )

      // Parse CC/BCC emails
      const ccEmails = formData.ccEmails
        ? formData.ccEmails
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)
        : []
      const bccEmails = formData.bccEmails
        ? formData.bccEmails
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)
        : []

      const emailData = {
        subject: formData.subject, // Contains "Welcome to {{userName}}"
        content: formData.content, // Contains "Hi {{userName}}, ..."
        contentType: formData.contentType,
        emailType: formData.emailType,
        recipientType: formData.recipientType,
        customRecipients:
          formData.recipientType === 'CUSTOM' ? customRecipients : undefined,
        templateId: formData.templateId || undefined,
        ccEmails: ccEmails.length > 0 ? ccEmails : undefined,
        bccEmails: bccEmails.length > 0 ? bccEmails : undefined,
        templateVariables:
          Object.keys(nonEmptyTemplateVariables).length > 0
            ? nonEmptyTemplateVariables
            : undefined,
      }

      await sendEmail(emailData)
      showNotification(
        'Email queued successfully. Check Email History for delivery status.',
        'success',
      )

      // Reset form
      setFormData(INITIAL_FORM_DATA)

      setTemplateVariables({})
    } catch (error) {
      showNotification(error.message || 'Failed to send email', 'error')
    } finally {
      setSending(false)
    }
  }


  return (
    <Box>
      <Typography variant='h5' sx={{ fontWeight: 600, mb: 3 }}>
        Compose Email
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column - Form */}

        <Grid item xs={12} md={showPreview ? 6 : 12}>
          {/* Template Variables Section */}
          {formData.templateId && Object.keys(templateVariables).length > 0 && (
            <Paper
              sx={{
                p: 3,
                mb: 3,
                bgcolor: '#fffbeb',
                border: '1px solid #fbbf24',
              }}
            >
              <Typography
                variant='h6'
                sx={{ mb: 2, fontWeight: 600, color: '#92400e' }}
              >
                Template Variables
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Fill in values for template variables. Leave empty to use
                defaults.
              </Typography>
              <Grid container spacing={2}>
                {Object.keys(templateVariables).map((varName) => (
                  <Grid item xs={12} sm={6} key={varName}>
                    <TextField
                      fullWidth
                      label={varName}
                      value={templateVariables[varName]}
                      onChange={handleTemplateVarChange(varName)}
                      placeholder={`Enter ${varName}`}
                      helperText={`Value for {{${varName}}}`}
                      size='small'
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
          <Paper sx={{ p: 3 }}>
            {/* Template Selector */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Template (Optional)</InputLabel>
              <Select
                value={formData.templateId}
                onChange={handleChange('templateId')}
                label='Select Template (Optional)'
                MenuProps={TEMPLATE_SELECT_MENU_PROPS}
              >
                <MenuItem value=''>None</MenuItem>
                {orderedTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    <Box
                      component='span'
                      title={template.name}
                      sx={{
                        display: 'block',
                        minWidth: 'max-content',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {template.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Subject */}
            <TextField
              fullWidth
              label='Subject'
              value={formData.subject}
              onChange={handleChange('subject')}
              required
              sx={{ mb: 3 }}
            />

            {/* Content Type */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={formData.contentType}
                onChange={handleChange('contentType')}
                label='Content Type'
              >
                {CONTENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Email Type</InputLabel>
              <Select
                value={formData.emailType}
                onChange={handleChange('emailType')}
                label='Email Type'
              >
                {EMAIL_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Content Editor */}
            <TextField
              fullWidth
              label='Email Content'
              multiline
              rows={12}
              value={formData.content}
              onChange={handleChange('content')}
              required
              placeholder={
                formData.contentType === 'MARKDOWN'
                  ? 'Enter Markdown content...'
                  : 'Enter HTML content...'
              }
              sx={{ mb: 3 }}
              helperText={
                formData.contentType === 'MARKDOWN'
                  ? 'Supports Markdown syntax'
                  : 'Supports HTML tags'
              }
            />

            {/* Recipient Type */}
            <FormControl component='fieldset' sx={{ mb: 3 }}>
              <FormLabel component='legend'>Recipients</FormLabel>
              <RadioGroup
                value={formData.recipientType}
                onChange={handleChange('recipientType')}
              >
                {RECIPIENT_TYPES.map((type) => (
                  <FormControlLabel
                    key={type.value}
                    value={type.value}
                    control={<Radio />}
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <span>{type.label}</span>
                        {formData.recipientType === type.value && (
                          <Chip
                            label={`${recipientCount} recipients`}
                            size='small'
                            color='primary'
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {formData.recipientType === 'CUSTOM' && (
              <TextField
                fullWidth
                label='Custom Recipients'
                multiline
                rows={4}
                value={formData.customRecipients}
                onChange={handleChange('customRecipients')}
                sx={{ mb: 3 }}
                placeholder='Enter email addresses separated by commas, semicolons, or new lines'
                helperText='Only valid email addresses will be accepted'
                required
              />
            )}

            {/* CC */}
            <TextField
              fullWidth
              label='CC (Comma-separated emails)'
              value={formData.ccEmails}
              onChange={handleChange('ccEmails')}
              sx={{ mb: 3 }}
              helperText='Optional: Enter email addresses separated by commas'
            />

            {/* BCC */}
            <TextField
              fullWidth
              label='BCC (Comma-separated emails)'
              value={formData.bccEmails}
              onChange={handleChange('bccEmails')}
              sx={{ mb: 3 }}
              helperText='Optional: Enter email addresses separated by commas'
            />

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant='outlined'
                startIcon={<Preview />}
                onClick={() => setShowPreview(!showPreview)}
                sx={{
                  borderColor: themeColors.primary,
                  color: themeColors.primary,
                  textTransform: 'none',
                  '&:hover': { borderColor: themeColors.hover, color: themeColors.hover },
                }}
              >
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </Button>
              <Button
                variant='contained'
                startIcon={sending ? <CircularProgress size={20} /> : <Send />}
                onClick={handleSend}
                disabled={sending}
                sx={{
                  bgcolor: themeColors.primary,
                  color: 'white',
                  '&:hover': { bgcolor: themeColors.hover },
                }}
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Preview */}
        {showPreview && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
                Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  To:{' '}
                  {
                    RECIPIENT_TYPES.find(
                      (t) => t.value === formData.recipientType,
                    )?.label
                  }{' '}
                  ({recipientCount} recipients)
                </Typography>
                <Typography variant='subtitle2' color='text.secondary'>
                  Email Type: {getEmailTypeLabel(formData.emailType)}
                </Typography>
                {formData.ccEmails && (
                  <Typography variant='subtitle2' color='text.secondary'>
                    CC: {formData.ccEmails}
                  </Typography>
                )}
                {formData.bccEmails && (
                  <Typography variant='subtitle2' color='text.secondary'>
                    BCC: {formData.bccEmails}
                  </Typography>
                )}
                <Typography variant='subtitle2' sx={{ fontWeight: 600, mt: 1 }}>
                  Subject:{' '}
                  {replaceVariablesInPreview(formData.subject) ||
                    '(No subject)'}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  minHeight: 400,
                  bgcolor: '#fafafa',
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{
                  __html: replaceVariablesInPreview(
                    formData.contentType === 'MARKDOWN'
                      ? formData.content // In production, convert markdown to HTML
                      : formData.content,
                  ),
                }}
              />
            </Paper>
          </Grid>
        )}
      </Grid>
      <Dialog
        open={emptyVariablesDialog.open}
        onClose={() => setEmptyVariablesDialog({ open: false, variables: [] })}
      >
        <DialogTitle>Use Default Template Values?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following template variable(s) are empty and default values will
            be used:
            <br />
            <br />
            <strong>{emptyVariablesDialog.variables.join(', ')}</strong>
            <br />
            <br />
            Continue sending this email?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setEmptyVariablesDialog({ open: false, variables: [] })
            }
            variant='outlined'
            sx={{
              borderColor: themeColors.primary,
              color: themeColors.primary,
              textTransform: 'none',
              '&:hover': { borderColor: themeColors.hover, color: themeColors.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceedWithEmptyVariables}
            variant='contained'
            sx={{
              bgcolor: themeColors.primary,
              color: 'white',
              '&:hover': { bgcolor: themeColors.hover, color: 'white' },
            }}
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, recipientCount: 0 })}
      >
        <DialogTitle>Confirm Email Send</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to send an email to{' '}
            <strong>{confirmDialog.recipientCount}</strong> recipient(s).
            <br />
            <br />
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, recipientCount: 0 })}
            variant='outlined'
            sx={{
              borderColor: themeColors.primary,
              color: themeColors.primary,
              textTransform: 'none',
              '&:hover': { borderColor: themeColors.hover, color: themeColors.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSend}
            variant='contained'
            sx={{
              bgcolor: themeColors.primary,
              color: 'white',
              '&:hover': {
                bgcolor: themeColors.hover,
                color: 'white',
              },
            }}
            autoFocus
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EmailComposer
